import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AccountActions from './AccountActions';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DoorlyChat, { OPEN_EVA_EVENT } from './DoorlyChat';
import { DOCUMENTS_SEEN_EVENT, documentsBadgeText, navRowBadgeText, homesLocked, statusWhileLoading } from '../constants/navBadges';
import JourneyHeader from './JourneyHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 THE 8-STEP TRACKER IS GONE (Jesse/Hannah, 2026-11-02 — the Home Shopping page redesign). Every borrower page shows
// the five-step line and bar instead (components/JourneyHeader; the stage → step map is constants/journeySteps, display
// only). It also covers the four stages the tracker had no slot for (conditional_approval, offer_submitted,
// clear_to_close, funded), whose borrowers saw no tracker at all.

// Pre-approval statuses where the sidebar Homes nav item is locked: constants/navBadges HOMES_LOCKED_STAGES (the
// deny-list, unchanged — an unmapped post-approval status unlocks by default).

// 🔑 THE LAST ANSWERS THIS SESSION, PER TOKEN (Hannah's review, Jesse 2026-11-02 item 7). Every page mounts its own
// AppShell, so without this the status and the counts started empty on every page change — Homes flashed locked and
// the badges blinked. A new AppShell starts from these and refreshes them in the background.
let remembered = { token: null, loanStatus: undefined, counts: null };
export const _forgetAppShell = () => { remembered = { token: null, loanStatus: undefined, counts: null }; };

// layout="guided": the guided flow's grey page (#f5f6fc) and no page padding — the page lays itself out in the guided
// column (the dashboard). Every other page keeps the default.
function AppShell({ children, layout = 'default' }) {
  const { token, user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // 🔑 undefined = not known yet; the last known answer for this token while it reloads (constants/navBadges).
  const [loanStatus, setLoanStatus] = useState(() => statusWhileLoading(remembered, token));
  const known = remembered.token === token ? remembered.counts : null;
  const [msgCount, setMsgCount] = useState(known ? known.msg : 0);
  const [alertCount, setAlertCount] = useState(known ? known.alert : 0);
  const [docUnread, setDocUnread] = useState(known ? known.doc : 0);
  const keepCount = (key, n) => {
    if (remembered.token !== token) remembered = { token, loanStatus: undefined, counts: null };
    remembered.counts = { msg: 0, alert: 0, doc: 0, ...(remembered.counts || {}), [key]: n };
  };

  // Fetch the user's current loan stage for progress bar + Homes lock
  useEffect(() => {
  if (!token) return;
  axios
  .get(`${API_URL}/api/borrower/status`, { headers: { Authorization: `Bearer ${token}` } })
  .then((r) => {
    remembered = { token, loanStatus: r.data || null, counts: remembered.token === token ? remembered.counts : null };
    setLoanStatus(r.data || null);
  })
  // 🚨 A failed reload keeps what was known (item 7): a blip never locks Homes.
  .catch(() => setLoanStatus(statusWhileLoading(remembered, token)));
  }, [token]);

  // Fetch unread message and alert counts
  useEffect(() => {
  if (!token) return;
  const headers = { Authorization: `Bearer ${token}` };
  axios.get(`${API_URL}/api/messages/unread-count`, { headers })
  .then((r) => { const n = r.data?.count ?? 0; keepCount('msg', n); setMsgCount(n); })
  .catch(() => {});
  axios.get(`${API_URL}/api/alerts/unread-count`, { headers })
  .then((r) => { const n = r.data?.count ?? 0; keepCount('alert', n); setAlertCount(n); })
  .catch(() => {});
  axios.get(`${API_URL}/api/documents/unread-count`, { headers })
  .then((r) => { const n = r.data?.count ?? 0; keepCount('doc', n); setDocUnread(n); })
  .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // 🔑 Item 6: the Documents page was seen (the server stamped the visit) — the badge drops now, not at the next page.
  useEffect(() => {
  const seen = () => { keepCount('doc', 0); setDocUnread(0); };
  window.addEventListener(DOCUMENTS_SEEN_EVENT, seen);
  return () => window.removeEventListener(DOCUMENTS_SEEN_EVENT, seen);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Use the deny-list directly against the raw loan_stage (not the
  // index) so an unmapped post-approval status — e.g. a future
  // offer_submitted / conditional_approval / clear_to_close — still
  // unlocks Homes. The old `stageIndex >= 3` check inverted that
  // safety: anything not in the map quietly locked.
  // 🚨 And only once CONFIRMED (item 7): while the status is unknown, Homes is not drawn locked.
  const homesIsLocked = homesLocked(loanStatus);
  const guidedLayout = layout === 'guided';
  const msgBadge = navRowBadgeText(msgCount);
  const alertBadge = navRowBadgeText(alertCount);
  const welcomeName = location.pathname === '/dashboard'
    ? ((user && user.firstName) || String((loanStatus && loanStatus.name) || '').split(' ')[0] || '') : undefined;
  const docBadge = documentsBadgeText(docUnread);


  const NAV_ITEMS = [
  {
  to: '/dashboard',
  label: 'Dashboard',
  external: false,
  locked: false,
  icon: (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
  ),
  },
  {
  to: '/documents',
  label: 'Documents',
  external: false,
  locked: false,
  badgeKey: 'documents',
  icon: (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
  ),
  },
  {
  to: '/homes',
  label: 'Homes',
  external: false,
  locked: homesIsLocked,
  lockReason: 'Available after approval',
  testId: 'nav-homes',
  // Roof + door silhouette (Heroicons v1 "home") — was the office-
  // building path which read as commercial. House shape matches the
  // page's "shop for a home" intent.
  icon: (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
  ),
  },
  {
  to: '/schedule',
  label: 'Schedule',
  external: false,
  locked: false,
  icon: (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
  ),
  },
  ];

  const SidebarContent = () => (
  <div className="flex flex-col h-full">
  {/* Logo */}
  <div className="px-6 py-5 border-b border-gray-100 flex-shrink-0">
  <Link to="/dashboard" className="inline-flex items-center gap-2.5" onClick={() => setMobileOpen(false)}>
  <img src={`${process.env.PUBLIC_URL}/doorly_logo_black.png`} alt="Doorly" style={{ height: '28px', width: 'auto' }} />
  </Link>
  </div>

  {/* Nav */}
  <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
  {/* Main pages */}
  {NAV_ITEMS.map((item) => {
  const active = location.pathname === item.to;
  if (item.locked) {
  return (
  <div
  key={item.to}
  title={item.lockReason}
  data-testid={item.testId || undefined}
  data-locked="true"
  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-300 cursor-not-allowed select-none"
  >
  <span className="text-gray-300">{item.icon}</span>
  {item.label}
  <svg className="w-3.5 h-3.5 ml-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
  </div>
  );
  }
  return (
  <Link
  key={item.to}
  to={item.to}
  data-testid={item.testId || undefined}
  data-locked={item.testId ? 'false' : undefined}
  onClick={() => setMobileOpen(false)}
  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
  active
  ? 'bg-[#3a6ad9]/10 text-[#3a6ad9]'
  : 'text-gray-600 hover:bg-gray-100 hover:text-black'
  }`}
  >
  <span className={`relative ${active ? 'text-[#3a6ad9]' : 'text-gray-400'}`}>
    {item.icon}
  </span>
  {item.label}
  {/* 🔑 ONE Documents badge (item 6): the NEW items since the borrower last opened Documents — never the total, never a
      second badge on the icon. */}
  {item.badgeKey === 'documents' && docBadge && (
    <span data-testid="nav-documents-badge" className="ml-auto bg-blue-100 text-blue-600 text-xs font-semibold px-1.5 py-0.5 rounded-full">{docBadge}</span>
  )}
  </Link>
  );
  })}

  <div className="pt-2 border-t border-gray-100 mt-2 space-y-1">
  {/* Messages & Notifications */}
  <Link
  to="/messages"
  onClick={() => setMobileOpen(false)}
  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
  location.pathname === '/messages'
  ? 'bg-[#3a6ad9]/10 text-[#3a6ad9]'
  : 'text-gray-600 hover:bg-gray-100 hover:text-black'
  }`}
  >
  <span className={`relative ${location.pathname === '/messages' ? 'text-[#3a6ad9]' : 'text-gray-400'}`}>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
  </span>
  Messages
  {/* 🔑 ONE badge, on the row (constants/navBadges navRowBadgeText — the Documents rule): never a second on the icon. */}
  {msgBadge && (
  <span data-testid="nav-messages-badge" className="ml-auto bg-blue-100 text-blue-600 text-xs font-semibold px-1.5 py-0.5 rounded-full">{msgBadge}</span>
  )}
  </Link>

  <Link
  to="/alerts"
  onClick={() => setMobileOpen(false)}
  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
  location.pathname === '/alerts'
  ? 'bg-[#3a6ad9]/10 text-[#3a6ad9]'
  : 'text-gray-600 hover:bg-gray-100 hover:text-black'
  }`}
  >
  <span className={`relative ${location.pathname === '/alerts' ? 'text-[#3a6ad9]' : 'text-gray-400'}`}>
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
  </span>
  Alerts
  {/* 🔑 ONE badge, on the row (constants/navBadges navRowBadgeText — the Documents rule): never a second on the icon. */}
  {alertBadge && (
  <span data-testid="nav-alerts-badge" className="ml-auto bg-blue-100 text-blue-600 text-xs font-semibold px-1.5 py-0.5 rounded-full">{alertBadge}</span>
  )}
  </Link>

  {/* Help — opens Eva. Was an external link to /contact; Eva can answer most of
      what people came here to ask, and reaches a human via her escalation tool
      when she can't. The help centre stays available as a secondary link below
      rather than being removed. */}
  <button
  type="button"
  onClick={() => { setMobileOpen(false); window.dispatchEvent(new Event(OPEN_EVA_EVENT)); }}
  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-black transition-colors text-left"
  >
  <span className="text-gray-400">
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
  </span>
  Help
  </button>

  {/* The help centre is MOVING FROM INTERCOM TO A NATIVE BUILD AT THIS SAME
    SUBDOMAIN. Do not remove this link during Intercom cleanup — the destination
    changes underneath it, the URL does not. See
    backend/knowledge/docs/help-export/MIGRATION.md for the cutover order. */}
  <a
  href="https://help.godoorly.com"
  target="_blank"
  rel="noreferrer"
  onClick={() => setMobileOpen(false)}
  className="flex items-center gap-3 px-3 py-1.5 pl-11 rounded-xl text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
  >
  Help center
  <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
  </a>

  {/* ⚠️ THE ONLY WAY INTO /admin FROM A DEVICE. The native app has no address
      bar, so before this existed staff could not reach the admin area at all —
      which is why a debug overlay had to be SHIPPED to borrowers just to take a
      header measurement. /admin is AdminEntry: it forwards to /admin/dashboard
      when an admin session exists and /admin/login when it does not, so one link
      covers both states.

      ⚠️ staffLinkHint DECIDES WHETHER THIS RENDERS AND NOTHING ELSE. It is a
      display claim on the borrower token (backend routes/auth.js makeToken).
      Everything behind this link is enforced server-side on the SEPARATE admin
      session — this link being visible grants exactly nothing. Deliberately not
      named isAdmin: that name is already an authorization input in several
      routes, and backend/test/staffLinkHintDisplayOnly.test.js keeps this one
      out of every decision path.

      ⚠️ Absent on tokens minted before this shipped, which reads as false. Staff
      must log out and back in once; the sliding renewal cannot add a claim the
      old token never had. */}
  {user?.staffLinkHint && (
  <a
  href="/admin"
  onClick={() => setMobileOpen(false)}
  className="flex items-center gap-3 px-3 py-1.5 pl-11 rounded-xl text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
  >
  Staff
  </a>
  )}
  </div>
  </nav>

  {/* ⚠️ ONE IMPLEMENTATION, SHARED WITH THE APPLICATION HEADER. This was inline
      here, which is why logout existed only on the routes AppShell wraps and
      not on /apply. See components/AccountActions.jsx. */}
  <AccountActions onNavigate={() => setMobileOpen(false)} />
  </div>
  );

  return (
  <div data-testid="app-shell" className={`min-h-screen flex${guidedLayout ? '' : ' bg-gray-50'}`} style={guidedLayout ? { backgroundColor: '#f5f6fc' } : undefined}>
  {/* Desktop sidebar */}
  <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-gray-200 fixed inset-y-0 left-0 z-30">
  <SidebarContent />
  </aside>

  {/* Mobile overlay */}
  {mobileOpen && (
  <div className="fixed inset-0 z-40 lg:hidden">
  <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
  {/* ⚠️ THE TOP INSET IS NOT OPTIONAL HERE. This aside sits inside a
      `fixed inset-0`, so it spans the status-bar area, and SidebarContent
      started at y=0 — putting the first nav item under the notch. The mobile
      topbar twelve lines below has always applied this inset; the drawer never
      did. Two sibling surfaces in one file, same concern, one covered and one
      not, is how this kind of gap survives review. */}
  <aside className="relative w-64 bg-white h-full shadow-xl overflow-y-auto"
         style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'var(--sa-bottom)' /* index.css --sa-bottom: the home indicator */ }}>
  <SidebarContent />
  </aside>
  </div>
  )}

  {/* Main column */}
  <div className="flex-1 min-w-0 lg:ml-60 flex flex-col min-h-screen">
  {/* Mobile topbar */}
  <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 flex items-center justify-between px-4"
       style={{ paddingTop: 'env(safe-area-inset-top, 0px)', height: 'var(--app-topbar-h)' }}>
  <Link to="/dashboard" className="inline-flex items-center gap-2">
  <img src={`${process.env.PUBLIC_URL}/doorly_logo_black.png`} alt="Doorly" style={{ height: '24px', width: 'auto' }} />
  </Link>
  <div className="flex items-center gap-3">
  {msgCount > 0 && (
  <Link to="/messages" className="relative p-1">
  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
  <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
  {msgBadge}
  </span>
  </Link>
  )}
  {alertCount > 0 && (
  <Link to="/alerts" className="relative p-1">
  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
  <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
  {alertBadge}
  </span>
  </Link>
  )}
  <button onClick={() => setMobileOpen(true)} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100">
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
  </button>
  </div>
  </div>

  {/* Offset spacer for the FIXED mobile topbar (mobile only). The topbar is
      position:fixed so it stays pinned on iOS instead of scrolling away; this
      spacer reserves its height in normal flow so the progress bar + page
      content start below it rather than behind it. Height matches the topbar
      calc exactly. lg:hidden because the topbar itself is lg:hidden. */}
  <div className="lg:hidden flex-shrink-0" aria-hidden="true"
       style={{ height: 'var(--app-topbar-h)' }} />

  {/* 🔑 The five-step line and bar (constants/journeySteps); "Welcome back" above it on the dashboard. */}
  <JourneyHeader status={loanStatus} welcomeName={welcomeName} />

  {/* Page content */}
  <main className={guidedLayout ? 'flex-1 pb-8' : 'flex-1 px-4 sm:px-6 lg:px-8 py-8'}>
  {children}
  </main>
  </div>

  {/* Doorly AI chat — fixed-position floating widget, available on every
      authenticated borrower page. Self-contained (manages its own state),
      gated by token presence. */}
  {token && <DoorlyChat token={token} userType="borrower" />}
  </div>
  );
}

export default AppShell;
